import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Dialog, DialogContent, DialogTitle, Grid, IconButton, Typography, DialogActions, ImageList, ImageListItem, Tooltip, Box } from '@mui/material';
import { Delete as DeleteIcon, CameraAlt as CameraAltIcon, UploadFile as UploadFileIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { setAlertMessage } from '../features/layoutSlice';
import Camera from './Camera';
import { useDeleteReviewImageMutation } from '../features/api/rest/apiReviewsImagesSlice';

function AddReviewPhoto({ images = [], reviewID = null, ean = null }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const fileInputRef = useRef(null);
  const isCameraActive = useSelector((state) => state.camera.camera);
  const [tmpImage, setTmpImage] = useState([]);
  const [totalImages, setTotalImages] = useState(0);
  const [imagesArray, setImagesArray] = useState([]);
  const [isFileReady, setIsFileReady] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [indexDelete, setIndexDelete] = useState(null);
  const [nameImage, setNameImage] = useState('');
  const [fullImage, setFullImage] = useState(false);

  const [deleteReviewImage] = useDeleteReviewImageMutation();

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpg, .jpeg, .png'],
    },
    multiple: true,
    onDrop: (acceptedFiles) => handleFileChange(acceptedFiles),
  });

  useEffect(() => {
    if (imagesArray.length > 0) {
      dispatch({ type: 'imagesReview/add', payload: { images: imagesArray, totalImages } });
    }
  }, [imagesArray]);

  const handleDialog = () => {
    setTmpImage([]);
    setIsFileReady(false);
    setOpen(!open);
  };

  const handleCloseDialogDelete = () => {
    setOpenDelete(false);
    setIndexDelete(null);
  };

  const handleFileChange = (files) => {
    if (isCameraActive) dispatch({ type: 'camera/stop' });
    const fileList = Array.from(files);
    if (fileList.length > 0) {
      fileList.map((file) => {
        handleFile(file);
      });
    } else {
      handleFile(files);
    }
    setTmpImage([...tmpImage]);
    setIsFileReady(true);
  };

  const handleFile = (file) => {
    if (file.type === 'image/jpeg' || file.type === 'image/png') {
      tmpImage.push(file);
    } else dispatch(setAlertMessage({ text: t('products.form.addProduct.addProductPhoto.errors.wrongfile'), type: 'error' }));
  };

  const handleAddImageToUpload = () => {
    setImagesArray([...imagesArray, ...tmpImage]);
    setTotalImages(totalImages + tmpImage.length);
    setTmpImage([]);
    setFileToUpload(true);
    setOpen(false);
  };

  const handleDeleteImage = (index) => {
    const tmpImagesArray = [...imagesArray];
    tmpImagesArray.splice(index, 1);
    setImagesArray(tmpImagesArray);
    setTotalImages(totalImages - 1);
    if (totalImages === 1) setFileToUpload(false);
  };

  const handleDeleteImageQuery = async () => {
    try {
      await deleteReviewImage({ ean, rpk: reviewID, ipk: indexDelete });
      dispatch(setAlertMessage({ text: t('reviews.images.delete.success'), type: 'success' }));
      handleCloseDialogDelete();
    } catch (error) {
      dispatch(setAlertMessage({ text: t('reviews.images.delete.error'), type: 'error' }));
    }
  };

  const listImageToUpload = imagesArray.map((image, index) => (
    <Grid container key={image?.name} sx={{ placeContent: 'center', mt: 1 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {image?.name}
      </Typography>
      <IconButton onClick={() => handleDeleteImage(index)} sx={{ pt: 0 }}>
        <DeleteIcon />
      </IconButton>
    </Grid>
  ));

  const listImageAdded = images.map((image) => (
    <Tooltip key={image?.id} title={image.image.split('/').pop()}>
      <Box>
        <ImageListItem
          key={image?.id}
          onClick={() => {
            setFullImage(true);
            setNameImage(image.image);
          }}
        >
          <img style={{ height: '20px' }} srcSet={`${image.image}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`} src={`${image.image}?w=164&h=164&fit=crop&auto=format`} alt={image.image.split('/').pop()} loading="lazy" />
        </ImageListItem>
        <IconButton
          onClick={() => {
            setIndexDelete(image.id);
            setOpenDelete(true);
          }}
          sx={{ pt: 0 }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Tooltip>
  ));

  const handleOpenCamera = () => {
    dispatch({ type: 'camera/start' });
  };

  return (
    <>
      {isCameraActive && <Camera onCapture={handleFileChange} imagefield={`reviewImage${Date.now()}`} />}
      <Button variant="outlined" component="label" sx={{ mb: 2 }} onClick={handleDialog}>
        {t('products.form.addProduct.addProductPhoto.addPhoto')}
      </Button>
      <Dialog open={open} onClose={handleDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ alignSelf: 'center' }}>{t('products.form.addProduct.addProductPhoto.addPhoto')}</DialogTitle>
        <DialogContent>
          <Grid component="form" container spacing={2} sx={{ pt: 1 }}>
            {/* eslint-disable react/jsx-props-no-spreading */}
            <Grid item xs={12} sm={6} {...getRootProps()} sx={{ placeContent: 'center', display: 'flex' }}>
              <Button variant="outlined" component="label" htmlFor="productImage" onClick={(e) => e.stopPropagation()}>
                {t('products.form.addProduct.addProductPhoto.selectPhoto')}
                <UploadFileIcon />
                <input {...getInputProps()} type="file" id="productImage" data-testid="UploadPhoto" accept=".jpg,.jpeg,.png" multiple hidden required onChange={() => handleFileChange(fileInputRef.current.files)} ref={fileInputRef} />
              </Button>
            </Grid>
            {/* eslint-enable react/jsx-props-no-spreading */}
            <Grid item xs={12} sm={1} sx={{ placeContent: 'center', display: 'flex' }}>
              <Typography sx={{ alignSelf: 'center', pl: { xs: 0 } }}>{t('common.separator')}</Typography>
            </Grid>
            <Grid item xs={12} sm={5} sx={{ placeContent: 'center', display: 'flex' }}>
              <Button onClick={handleOpenCamera}>
                <CameraAltIcon />
              </Button>
            </Grid>
          </Grid>
          {isFileReady &&
            tmpImage.map((image) => (
              <Typography key={image?.name} sx={{ textAlign: 'center', pt: 2 }}>
                {image?.name}
              </Typography>
            ))}
          <Grid sx={{ placeContent: 'center', display: 'flex' }}>
            <Button variant="contained" onClick={handleAddImageToUpload} disabled={!isFileReady} sx={{ mt: 2 }} data-testid="FirstAddPhoto">
              {t('products.form.addProduct.addProductPhoto.addPhoto')}
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
      {fileToUpload && (
        <Card variant="outlined" sx={{ width: '100%', height: '100%', display: 'grid', mb: 2, textAlign: 'center', fontWeight: 'bold', fontSize: 'large' }}>
          {t('reviews.images.photoToAdd')}
          {listImageToUpload}
        </Card>
      )}
      {images && images.length > 0 && (
        <Card variant="outlined" sx={{ width: '100%', height: '100%', mb: 2, textAlign: 'center', fontWeight: 'bold', fontSize: 'large' }}>
          {t('reviews.images.photoAdded')}
          <ImageList sx={{ height: 450, mt: 2 }} cols={3} rowHeight={150} gap={10}>
            {listImageAdded}
          </ImageList>
        </Card>
      )}
      <Dialog open={openDelete} onClose={() => handleCloseDialogDelete()} fullWidth maxWidth="sm">
        <DialogTitle sx={{ alignSelf: 'center' }}>{t('reviews.images.delete.title')}</DialogTitle>
        <DialogActions>
          <Button onClick={() => handleCloseDialogDelete()}>{t('common.actions.cancel')}</Button>
          <Button onClick={handleDeleteImageQuery}>{t('common.actions.confirm')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={fullImage}
        onClose={() => {
          setFullImage(false);
          setNameImage('');
        }}
        fullWidth
        maxWidth="sm"
        fullScreen={fullImage}
      >
        <CancelIcon
          sx={{ position: 'absolute', top: '2%', left: '96%', cursor: 'pointer', color: 'red', fontSize: '2rem' }}
          onClick={() => {
            setFullImage(false);
            setNameImage('');
          }}
        />
        <img src={`${nameImage}`} alt={nameImage.split('/').pop()} loading="lazy" data-testid="FullImage" />
      </Dialog>
    </>
  );
}

export default AddReviewPhoto;

const imageShape = PropTypes.shape({
  id: PropTypes.number,
  image: PropTypes.string,
});

AddReviewPhoto.propTypes = {
  images: PropTypes.arrayOf(imageShape),
  reviewID: PropTypes.number,
  ean: PropTypes.string,
};
